<template>
	<v-expansion-panels v-model="open" flat class="promo-step__panels">
		<v-expansion-panel value="offer" rounded="lg" :bg-color="forecast.active ? 'forecast-surface' : undefined" :class="['border', status ? `promo-step__status promo-step__status--${status}` : '']">
			<v-expansion-panel-title hide-actions :hover="false">
				<template #default="{ expanded }">
					<div class="d-flex align-center flex-wrap ga-3 w-100">
						<v-icon>{{ expanded ? 'expand_less' : 'expand_more' }}</v-icon>
						<div class="d-flex flex-wrap align-center ga-2 flex-grow-1 text-title-small font-weight-bold text-high-emphasis">
							{{ offer.label }}
							<v-chip v-if="offer.mechanismLabel" color="warning" size="small" label>{{ offer.mechanismLabel }}</v-chip>
						</div>
						<div v-if="!compact" class="promo-step__offer-totals">
							<div>
								<div class="text-body-small text-medium-emphasis">{{ t('PromoTotalVOL') }}</div>
								<div class="text-body-medium font-weight-bold">{{ format.uc(totals.volumeUC) }}{{ objectives.objectiveVOL ? ` / ${format.uc(objectives.objectiveVOL)}` : '' }}</div>
							</div>
							<div>
								<div class="text-body-small text-medium-emphasis">{{ t('TotalUB') }}</div>
								<v-chip v-if="totals.ub === null" color="primary" size="small" append-icon="info" :title="t('ProductsHaveDifferentPackaging')">{{ t('MixtedUnits') }}</v-chip>
								<v-chip v-else color="primary" size="small" class="font-weight-bold">{{ format.number(totals.ub.total) }} {{ containerLabel(totals.ub.containerId) }}</v-chip>
							</div>
							<div>
								<div class="text-body-small text-medium-emphasis">{{ t('PromoTotalCA') }}</div>
								<div class="text-body-medium font-weight-bold">{{ format.money(totals.value) }}{{ objectives.objectiveCA ? ` / ${format.money(objectives.objectiveCA)}` : '' }}</div>
							</div>
						</div>
						<v-btn v-if="offerKey && !snapshot.readonly" prepend-icon="delete" variant="outlined" color="error" @click.stop="ops.removeLocalOffer(offerRef)">{{ t('RemoveOffer') }}</v-btn>
					</div>
				</template>
			</v-expansion-panel-title>
			<v-expansion-panel-text>
				<div class="d-flex flex-column ga-3 pt-3">
					<PromoForecastPanel :campaign="campaign" :offer-ref="offerRef" />
					<PromoPlanPanel :campaign="campaign" :offer-ref="offerRef" />
					<div v-if="showPoints" class="promo-step__section promo-step__section--points">
						<OfferPointsBanner :context="pointsContext" :hide-claim="pointsContext.forecastActive" @history-change="(value) => (hasPointsHistory = value)" />
					</div>

					<div v-if="compact" class="promo-step__offer-metrics">
						<MetricTile :label="t('PromoTotalVOL')" :value="format.uc(totals.volumeUC)" />
						<MetricTile :label="t('TotalUB')">
							<v-chip v-if="totals.ub === null" color="secondary" size="x-small" append-icon="info" :title="t('ProductsHaveDifferentPackaging')">{{ t('MixtedUnits') }}</v-chip>
							<template v-else>{{ format.number(totals.ub.total) }} {{ containerLabel(totals.ub.containerId) }}</template>
						</MetricTile>
						<MetricTile :label="t('PromoTotalCA')" :value="format.money(totals.value)" />
					</div>

					<v-row density="compact">
						<template v-if="offerKey">
							<v-col cols="6" :md="compact ? 6 : 3">
								<DatePickerControl :key="`start-${resetKey}`" :model-value="format.input(offerInfo?.startDate)" :label="t('LocaleOfferStartDate')" :min="dateBounds.start.min" :max="dateBounds.start.max" :disabled="!datesEditable" @update:model-value="(value) => onLocalDate('startDate', value)" />
							</v-col>
							<v-col cols="6" :md="compact ? 6 : 3">
								<DatePickerControl :key="`end-${resetKey}`" :model-value="format.input(offerInfo?.endDate)" :label="t('LocaleOfferEndDate')" :min="dateBounds.end.min" :max="dateBounds.end.max" :disabled="!datesEditable" clearable @update:model-value="(value) => onLocalDate('endDate', value)" />
							</v-col>
						</template>
						<v-col cols="12" :sm="offerKey ? 12 : 6" :md="compact ? 12 : 3">
							<DatePickerControl :key="`statement-${resetKey}`" :model-value="statementEndDate" :label="t('OfferStatementEndDate')" :min="format.input(statementBounds.min)" :max="format.input(statementBounds.max)" :disabled="snapshot.readonly" clearable @update:model-value="onStatementEndDate" />
						</v-col>
						<v-col cols="12">
							<v-textarea :model-value="comment" :label="t(offerKey ? 'PrivatePromoComments' : 'Comment')" rows="1" auto-grow variant="outlined" :disabled="snapshot.readonly" :counter="offerKey ? 500 : undefined" @change="(event) => ops.setOfferComment(offerRef, event.target.value)" />
						</v-col>
					</v-row>

					<div v-if="localPhotos && (localPhotos.nego.visible || localPhotos.statement.visible)" class="d-flex flex-wrap ga-6">
						<div v-if="localPhotos.nego.visible">
							<div class="text-body-small text-medium-emphasis mb-1">{{ t('photos') }}</div>
							<PromoPhotoTiles :items="localPhotos.nego.items" :can-add="localPhotos.nego.canAdd" :can-edit="localPhotos.nego.canEdit" @upload="(replace) => ops.uploadPhoto({ campaignId: campaign.id, offerKey, isStatement: false, replace })" @delete="(photo) => ops.deletePhoto({ campaignId: campaign.id, offerKey, isStatement: false, photo })" />
						</div>
						<div v-if="localPhotos.statement.visible">
							<div class="text-body-small text-medium-emphasis mb-1">{{ t('PhotosStatement') }}</div>
							<PromoPhotoTiles :items="localPhotos.statement.items" :can-add="localPhotos.statement.canAdd" :can-edit="localPhotos.statement.canEdit" @upload="(replace) => ops.uploadPhoto({ campaignId: campaign.id, offerKey, isStatement: true, replace })" @delete="(photo) => ops.deletePhoto({ campaignId: campaign.id, offerKey, isStatement: true, photo })" />
						</div>
					</div>

					<v-alert v-for="(warning, index) in offerWarnings" :key="`w${index}`" type="warning" variant="tonal" density="compact">{{ formatPromoMessage(warning.display, t) }}</v-alert>
					<v-alert v-for="(error, index) in offerLevelErrors" :key="`e${index}`" type="error" variant="tonal" density="compact">{{ formatPromoMessage(error.display, t) }}</v-alert>

					<template v-if="phase !== PHASE.NONE">
						<PromoOfferToolbar :campaign="campaign" :offer="offer" :offer-ref="offerRef" :phase="phase" />
						<PromoProductList v-if="compact" :campaign="campaign" :offer="offer" :offer-ref="offerRef" :phase="phase" />
						<PromoProductTable v-else :campaign="campaign" :offer="offer" :offer-ref="offerRef" :phase="phase" />
					</template>
				</div>
			</v-expansion-panel-text>
		</v-expansion-panel>
	</v-expansion-panels>
</template>

<script setup>
import { computed, ref } from 'vue';
import { usePromoStep } from '@/composables/usePromoStep';
import { PHASE } from '@/utils/domain/promo/promoPhase';
import { displayPhase, forecastKeepsDatesEditable, getOfferForecastState } from '@/utils/domain/promo/promoForecast';
import { offerStatus } from '@/utils/domain/promo/promoStatus';
import { offerObjectives, offerTotals } from '@/utils/domain/promo/promoValues';
import { getOfferInfo, negoRowsOf, toRef } from '@/utils/domain/promo/promoModel';
import { buildOfferPointsContext } from '@/utils/domain/promo/promoPoints';
import { localOfferDateBounds, localOfferDatesEditable } from '@/utils/domain/promo/promoLocalOffer';
import { offerComment, offerInfoOf, statementEndDateBounds, statementEndDateValue } from '@/utils/domain/promo/promoOfferInfo';
import { localPhotoRules } from '@/utils/domain/promo/promoPhotos';
import { formatPromoMessage, offerErrors } from '@/utils/domain/promo/promoValidation';
import { isScoringMatrixAvailable } from '@/services/pointsMatrixService';
import OfferPointsBanner from '@/components/points/OfferPointsBanner.vue';
import MetricTile from '@/components/common/MetricTile.vue';
import DatePickerControl from '@/components/controls/DatePickerControl.vue';
import PromoPhotoTiles from './PromoPhotoTiles.vue';
import PromoOfferToolbar from './PromoOfferToolbar.vue';
import PromoProductTable from './PromoProductTable.vue';
import PromoProductList from './PromoProductList.vue';
import PromoForecastPanel from './PromoForecastPanel.vue';
import PromoPlanPanel from './PromoPlanPanel.vue';

const props = defineProps({
	campaign: { type: Object, required: true },
	offer: { type: Object, required: true },
	offerKey: { type: String, default: null },
});

const { snapshot, validation, compact, ops, format, t } = usePromoStep();
const open = ref(null);
const hasPointsHistory = ref(false);
const resetKey = ref(0);

const offerRef = computed(() => toRef(props.campaign.id, props.offer.id, props.offerKey));
const offerInfo = computed(() => getOfferInfo(snapshot.value, props.offerKey));
const phase = computed(() => displayPhase(snapshot.value, props.campaign, offerRef.value));
const forecast = computed(() => getOfferForecastState(snapshot.value, props.campaign, offerRef.value));
const status = computed(() => offerStatus(snapshot.value, props.campaign, props.offer.id, props.offerKey));
const totals = computed(() => offerTotals(negoRowsOf(snapshot.value, offerRef.value), offerRef.value));
const objectives = computed(() => (props.offerKey ? {} : offerObjectives(props.campaign, props.offer.id)));
const pointsContext = computed(() => buildOfferPointsContext(snapshot.value, props.campaign, props.offer.id, props.offerKey));
const showPoints = computed(() => pointsContext.value.scoringEnabled && isScoringMatrixAvailable());
const comment = computed(() => offerComment(snapshot.value, offerRef.value));
const statementEndDate = computed(() => statementEndDateValue(snapshot.value, offerRef.value));
const statementBounds = computed(() => statementEndDateBounds(props.campaign, offerInfoOf(snapshot.value, offerRef.value)));
const dateBounds = computed(() => localOfferDateBounds(snapshot.value, props.campaign, offerInfo.value));
const datesEditable = computed(() => !!offerInfo.value && localOfferDatesEditable(snapshot.value, offerInfo.value, props.campaign));
const localPhotos = computed(() => (props.offerKey && offerInfo.value ? localPhotoRules(snapshot.value, props.campaign, offerInfo.value, forecastKeepsDatesEditable(snapshot.value, props.campaign, offerRef.value)) : null));
const offerLevelErrors = computed(() => offerErrors(validation.value, offerRef.value).filter((e) => e.level === 'offer' && e.display));
const offerWarnings = computed(() => (validation.value?.warnings || []).filter((w) => w.offerKey === props.offerKey && props.offerKey));

const containerLabel = (id) => snapshot.value.refs.containers.find((c) => c.id === id)?.label || '';

async function onLocalDate(field, value) {
	if (!(await ops.setLocalOfferDate(offerRef.value, field, value, hasPointsHistory.value))) resetKey.value += 1;
}

function onStatementEndDate(value) {
	if (!ops.setStatementEndDate(offerRef.value, value)) resetKey.value += 1;
}
</script>
